/**
 * @file DeliveryQuoteForm.tsx
 * @description Форма расчёта доставки: куда везём + места (phase19.12 шаг 5).
 *
 * Переиспользуется в двух местах: Sheet «Расчёт доставки» в карточке заказа и
 * справочный калькулятор в настройках перевозчика (там нет позиций заказа —
 * места заполняются руками).
 */

import { Loader2, Plus } from "lucide-react";

import { DeliveryDestinationFields } from "./DeliveryDestinationFields";
import { DeliveryPackageCard } from "./DeliveryPackageCard";
import { Button } from "../ui/button";
import type { OrderDeliveryLineDto } from "../../lib/api-orders";
import {
  createPackageDraft,
  formatWeight,
  totalWeightG,
  type DeliveryPackageDraft,
  type DeliveryQuoteFormValue,
} from "../../lib/delivery-quote";

interface Props {
  value: DeliveryQuoteFormValue;
  onChange: (patch: Partial<DeliveryQuoteFormValue>) => void;
  /** Позиции заказа для раскладки по местам. */
  items?: OrderDeliveryLineDto[];
  /** ПВЗ из заказа — для отметки «тот же, что выбрал клиент». */
  clientPickupPointCode?: string | null;
  /** Вес по позициям из последнего расчёта. */
  autoWeightG?: number | null;
  running?: boolean;
  disabled?: boolean;
  onRun: () => void;
}

export function DeliveryQuoteForm({
  value,
  onChange,
  items,
  clientPickupPointCode,
  autoWeightG,
  running,
  disabled,
  onRun,
}: Props) {
  const patchPackage = (index: number, patch: Partial<DeliveryPackageDraft>): void => {
    onChange({
      packages: value.packages.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    });
  };

  const addPackage = (): void => {
    onChange({ packages: [...value.packages, createPackageDraft()] });
  };

  /** Позиции удалённого места возвращаются в первое — «не распределено» нет. */
  const removePackage = (index: number): void => {
    const removed = value.packages[index];
    const rest = value.packages.filter((_, i) => i !== index);
    const first = rest[0];
    if (!first || !removed) return;
    rest[0] = {
      ...first,
      orderItemIds: [...new Set([...first.orderItemIds, ...removed.orderItemIds])],
    };
    onChange({ packages: rest });
  };

  /** orderItemId → номер места, в котором позиция уже лежит. */
  const placedElsewhere = (index: number): Map<string, number> => {
    const map = new Map<string, number>();
    value.packages.forEach((p, i) => {
      if (i === index) return;
      p.orderItemIds.forEach((id) => map.set(id, i + 1));
    });
    return map;
  };

  const total = totalWeightG(value.packages);

  return (
    <div className="flex flex-col gap-5">
      <DeliveryDestinationFields
        value={value}
        onChange={onChange}
        clientPickupPointCode={clientPickupPointCode}
        disabled={disabled}
      />

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
            Места ({value.packages.length})
          </div>
          <span className="text-xs text-on-surface-variant">
            {total > 0 ? `всего ${formatWeight(total)}` : "вес по позициям"}
          </span>
        </div>
        {value.packages.map((pkg, index) => (
          <DeliveryPackageCard
            key={pkg.key}
            index={index}
            value={pkg}
            items={items}
            placedElsewhere={placedElsewhere(index)}
            autoWeightG={autoWeightG}
            disabled={disabled}
            onChange={(patch) => patchPackage(index, patch)}
            onRemove={index === 0 ? undefined : () => removePackage(index)}
          />
        ))}
        <Button
          variant="outline"
          onClick={addPackage}
          disabled={disabled}
          className="min-h-11 self-start"
        >
          <Plus className="mr-1 size-4" />
          Добавить место
        </Button>
      </section>

      <Button onClick={onRun} disabled={disabled || running} className="min-h-11">
        {running ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : null}
        {running ? "Считаем…" : "Рассчитать"}
      </Button>
    </div>
  );
}
